import React, { useEffect, useState } from 'react';
import { newsAPI, uploadAPI } from '../../services/api';
import '../admin/AdminComponents.css';

const DEFAULT_CONFIG = {
  landingMode: 'manual',
  limits: {
    titleMaxLength: 60,
    descriptionMaxLength: 100
  },
  limitRanges: {
    titleMaxLength: { min: 30, max: 80 },
    descriptionMaxLength: { min: 50, max: 160 }
  },
  actionTypes: [
    { value: 'visit', label: 'Visit' },
    { value: 'read_more', label: 'Read More' },
    { value: 'facebrowser_post', label: 'Facebrowser Post' },
    { value: 'online_shop', label: 'Online Shop' },
    { value: 'careers', label: 'Careers' },
    { value: 'none', label: 'No Button' },
    { value: 'more_info', label: 'More Info' },
    { value: 'custom', label: 'Custom' }
  ],
  categories: ['Company News', 'Vague', 'Veloura Studios', 'Careers', 'Events']
};

const getDefaultForm = () => ({
  title: '',
  description: '',
  fullArticleUrl: '',
  backgroundImageUrl: '',
  actionType: 'read_more',
  customButtonText: '',
  category: 'Company News',
  submittedAt: new Date().toISOString().slice(0, 10),
  displayOrder: 0,
  landingPageOrder: '',
  active: true
});

const NewsManagement = () => {
  const [articles, setArticles] = useState([]);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [limitDraft, setLimitDraft] = useState(DEFAULT_CONFIG.limits);
  const [landingModeDraft, setLandingModeDraft] = useState(DEFAULT_CONFIG.landingMode);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState(getDefaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [savingLandingMode, setSavingLandingMode] = useState(false);

  const loadArticles = async () => {
    const response = await newsAPI.getAll();
    if (response.success) {
      setArticles(response.data || []);
    }
  };

  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);
        const [articlesResponse, configResponse] = await Promise.all([
          newsAPI.getAll(),
          newsAPI.getConfig()
        ]);

        if (articlesResponse.success) {
          setArticles(articlesResponse.data || []);
        }
        if (configResponse.success && configResponse.data) {
          const nextConfig = { ...DEFAULT_CONFIG, ...configResponse.data };
          setConfig(nextConfig);
          setLimitDraft(nextConfig.limits);
          setLandingModeDraft(nextConfig.landingMode);
        }
      } catch (error) {
        console.error('Error loading news management:', error);
        alert(error.message || 'Failed to load news articles');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const openCreateForm = () => {
    setEditingArticle(null);
    setFormData(getDefaultForm());
    setShowForm(true);
  };

  const openEditForm = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title || '',
      description: article.description || '',
      fullArticleUrl: article.full_article_url || '',
      backgroundImageUrl: article.background_image_url || '',
      actionType: article.action_type || 'read_more',
      customButtonText: article.custom_button_text || '',
      category: article.category || 'Company News',
      submittedAt: article.submitted_at
        ? new Date(article.submitted_at).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      displayOrder: article.display_order || 0,
      landingPageOrder: article.landing_page_order || '',
      active: Boolean(article.active)
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (uploadingImage) return;
    setShowForm(false);
    setEditingArticle(null);
    setFormData(getDefaultForm());
  };

  const handleImageUpload = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      const response = await uploadAPI.uploadImage(file, 'news');
      setFormData((prev) => ({ ...prev, backgroundImageUrl: response.data.imageUrl }));
    } catch (error) {
      alert(error.message || 'Failed to upload news image');
    } finally {
      setUploadingImage(false);
      input.value = '';
    }
  };

  const handleActionTypeChange = (actionType) => {
    setFormData((prev) => {
      let fullArticleUrl = prev.fullArticleUrl;
      if (actionType === 'none') {
        fullArticleUrl = '';
      } else if (!fullArticleUrl && actionType === 'online_shop') {
        fullArticleUrl = '/shop';
      } else if (!fullArticleUrl && actionType === 'careers') {
        fullArticleUrl = '/careers';
      }

      return {
        ...prev,
        actionType,
        fullArticleUrl,
        customButtonText: actionType === 'custom' ? prev.customButtonText : ''
      };
    });
  };

  const handleSaveLimits = async (event) => {
    event.preventDefault();
    try {
      setSavingLimits(true);
      const response = await newsAPI.updateConfig({
        titleMaxLength: Number(limitDraft.titleMaxLength),
        descriptionMaxLength: Number(limitDraft.descriptionMaxLength)
      });
      if (response.success && response.data) {
        setConfig((prev) => ({ ...prev, ...response.data }));
        setLimitDraft(response.data.limits);
      }
    } catch (error) {
      console.error('Error saving news limits:', error);
      alert(error.message || 'Failed to save news content limits');
    } finally {
      setSavingLimits(false);
    }
  };

  const handleSaveLandingMode = async (event) => {
    event.preventDefault();
    try {
      setSavingLandingMode(true);
      const response = await newsAPI.updateLandingConfig({ landingMode: landingModeDraft });
      if (response.success && response.data) {
        setConfig((prev) => ({ ...prev, ...response.data }));
        setLandingModeDraft(response.data.landingMode);
      }
    } catch (error) {
      console.error('Error saving landing news mode:', error);
      alert(error.message || 'Failed to save landing page news mode');
    } finally {
      setSavingLandingMode(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (uploadingImage || submitting) return;
    const title = formData.title.trim();
    const description = formData.description.trim();

    if (!title) {
      alert('Title is required.');
      return;
    }
    if (title.length > config.limits.titleMaxLength) {
      alert(`Title cannot exceed ${config.limits.titleMaxLength} characters.`);
      return;
    }
    if (description.length > config.limits.descriptionMaxLength) {
      alert(`Description cannot exceed ${config.limits.descriptionMaxLength} characters.`);
      return;
    }
    if (formData.actionType !== 'none' && !formData.fullArticleUrl.trim()) {
      alert('A destination URL is required for the selected button.');
      return;
    }
    if (formData.actionType === 'custom' && !formData.customButtonText.trim()) {
      alert('Custom button text is required.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        title,
        description,
        displayOrder: Number(formData.displayOrder) || 0,
        landingPageOrder: formData.landingPageOrder === ''
          ? null
          : Number(formData.landingPageOrder)
      };

      const response = editingArticle
        ? await newsAPI.update(editingArticle.id, payload)
        : await newsAPI.create(payload);

      if (response.success) {
        closeForm();
        await loadArticles();
      }
    } catch (error) {
      console.error('Error saving news article:', error);
      alert(error.message || 'Failed to save news article');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) {
      return;
    }

    try {
      await newsAPI.delete(id);
      await loadArticles();
    } catch (error) {
      console.error('Error deleting news article:', error);
      alert(error.message || 'Failed to delete news article');
    }
  };

  const getActionLabel = (article) => {
    if (article.action_type === 'custom') {
      return article.custom_button_text || 'Custom';
    }
    return config.actionTypes.find((action) => action.value === article.action_type)?.label || 'Read More';
  };

  if (loading) {
    return (
      <div className="admin-section">
        <div className="loading-message">Loading news articles...</div>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">News</h2>
        <div className="admin-section-actions">
          <button className="admin-button primary" onClick={openCreateForm}>
            + Add Article
          </button>
        </div>
      </div>

      <form className="admin-settings-card" onSubmit={handleSaveLimits}>
        <h3 className="admin-settings-card-title">Content Limits</h3>
        <p className="admin-settings-card-desc">
          These limits keep every public news card aligned. Existing posts are preserved until they are edited.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <div className="admin-form-group">
            <label className="admin-form-label">Title Character Limit</label>
            <input
              className="admin-form-input"
              type="number"
              min={config.limitRanges.titleMaxLength.min}
              max={config.limitRanges.titleMaxLength.max}
              value={limitDraft.titleMaxLength}
              onChange={(e) => setLimitDraft((prev) => ({ ...prev, titleMaxLength: e.target.value }))}
              required
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Description Character Limit</label>
            <input
              className="admin-form-input"
              type="number"
              min={config.limitRanges.descriptionMaxLength.min}
              max={config.limitRanges.descriptionMaxLength.max}
              value={limitDraft.descriptionMaxLength}
              onChange={(e) => setLimitDraft((prev) => ({ ...prev, descriptionMaxLength: e.target.value }))}
              required
            />
          </div>
        </div>
        <button className="admin-button secondary" type="submit" disabled={savingLimits}>
          {savingLimits ? 'Saving...' : 'Save Limits'}
        </button>
      </form>

      <form className="admin-settings-card" onSubmit={handleSaveLandingMode}>
        <h3 className="admin-settings-card-title">Landing Page News</h3>
        <p className="admin-settings-card-desc">
          Manual mode uses each article's Landing Page Order ID. Automatic mode shuffles up to four active posts on every page refresh.
        </p>
        <div className="admin-form-group">
          <label className="admin-form-label">Selection Mode</label>
          <select
            className="admin-form-input"
            value={landingModeDraft}
            onChange={(e) => setLandingModeDraft(e.target.value)}
          >
            <option value="manual">Manual Order</option>
            <option value="automatic">Automatic Rotation</option>
          </select>
        </div>
        <button className="admin-button secondary" type="submit" disabled={savingLandingMode}>
          {savingLandingMode ? 'Saving...' : 'Save Landing Mode'}
        </button>
      </form>

      {articles.length === 0 ? (
        <div className="admin-empty-state">
          <p>No articles found. Create the first news post.</p>
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Submitted</th>
                <th>Button</th>
                <th>Order</th>
                <th>Landing Slot</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article.id}>
                  <td>
                    <strong>{article.title}</strong>
                    {article.description && (
                      <div className="admin-description">{article.description}</div>
                    )}
                  </td>
                  <td>{article.category || 'Company News'}</td>
                  <td>
                    {article.submitted_at
                      ? new Date(article.submitted_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })
                      : 'N/A'}
                  </td>
                  <td>{getActionLabel(article)}</td>
                  <td>{article.display_order || 0}</td>
                  <td>{article.landing_page_order || 'Not Featured'}</td>
                  <td>
                    <span className={`admin-status ${article.active ? 'active' : 'inactive'}`}>
                      {article.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        type="button"
                        className="admin-icon-button"
                        onClick={() => openEditForm(article)}
                        title="Edit"
                        aria-label={`Edit ${article.title}`}
                      >
                        &#9998;
                      </button>
                      <button
                        type="button"
                        className="admin-icon-button danger"
                        onClick={() => handleDelete(article.id)}
                        title="Delete"
                        aria-label={`Delete ${article.title}`}
                      >
                        &times;
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="admin-modal-overlay" onClick={closeForm}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">
                {editingArticle ? 'Edit News Article' : 'Add News Article'}
              </h3>
              <button className="admin-modal-close" onClick={closeForm} aria-label="Close">&times;</button>
            </div>
            <form className="admin-modal-body" onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label className="admin-form-label">Title *</label>
                <input
                  className="admin-form-input"
                  value={formData.title}
                  maxLength={config.limits.titleMaxLength}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  required
                />
                <small className="admin-form-hint">
                  {formData.title.length} / {config.limits.titleMaxLength} characters
                </small>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Category *</label>
                <select
                  className="admin-form-input"
                  value={formData.category}
                  onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                  required
                >
                  {config.categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Submission Date *</label>
                <input
                  className="admin-form-input"
                  type="date"
                  value={formData.submittedAt}
                  onChange={(e) => setFormData((prev) => ({ ...prev, submittedAt: e.target.value }))}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Brief Description</label>
                <textarea
                  className="admin-form-textarea"
                  rows={4}
                  value={formData.description}
                  maxLength={config.limits.descriptionMaxLength}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                />
                <small className="admin-form-hint">
                  {formData.description.length} / {config.limits.descriptionMaxLength} characters
                </small>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Button Type *</label>
                <select
                  className="admin-form-input"
                  value={formData.actionType}
                  onChange={(e) => handleActionTypeChange(e.target.value)}
                  required
                >
                  {config.actionTypes.map((action) => (
                    <option key={action.value} value={action.value}>{action.label}</option>
                  ))}
                </select>
              </div>

              {formData.actionType === 'custom' && (
                <div className="admin-form-group">
                  <label className="admin-form-label">Custom Button Text *</label>
                  <input
                    className="admin-form-input"
                    value={formData.customButtonText}
                    maxLength={30}
                    onChange={(e) => setFormData((prev) => ({ ...prev, customButtonText: e.target.value }))}
                    required
                  />
                  <small className="admin-form-hint">
                    {formData.customButtonText.length} / 30 characters
                  </small>
                </div>
              )}

              {formData.actionType !== 'none' && (
                <div className="admin-form-group">
                  <label className="admin-form-label">Destination URL *</label>
                  <input
                    className="admin-form-input"
                    type="text"
                    value={formData.fullArticleUrl}
                    onChange={(e) => setFormData((prev) => ({ ...prev, fullArticleUrl: e.target.value }))}
                    placeholder="https://... or /page"
                    required
                  />
                </div>
              )}

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="news-background-url">Background Image URL</label>
                <input
                  id="news-background-url"
                  className="admin-form-input"
                  type="url"
                  disabled={uploadingImage || submitting}
                  value={formData.backgroundImageUrl}
                  onChange={(e) => setFormData((prev) => ({ ...prev, backgroundImageUrl: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="news-background-upload">Or Upload Background Image</label>
                <input
                  id="news-background-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  disabled={uploadingImage || submitting}
                  onChange={handleImageUpload}
                />
                <small className="admin-form-hint" role="status">
                  {uploadingImage ? 'Uploading image...' : 'JPEG, PNG, GIF or WebP. Maximum 5MB.'}
                </small>
              </div>

              {formData.backgroundImageUrl && (
                <div className="admin-settings-preview">
                  <img src={formData.backgroundImageUrl} alt="News background preview" />
                </div>
              )}

              <div className="admin-form-group">
                <label className="admin-form-label">Display Order</label>
                <input
                  className="admin-form-input"
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData((prev) => ({ ...prev, displayOrder: e.target.value }))}
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Landing Page Order ID</label>
                <select
                  className="admin-form-input"
                  value={formData.landingPageOrder}
                  onChange={(e) => setFormData((prev) => ({ ...prev, landingPageOrder: e.target.value }))}
                >
                  <option value="">Not Featured</option>
                  <option value="1">1 - First Position</option>
                  <option value="2">2 - Second Position</option>
                  <option value="3">3 - Third Position</option>
                  <option value="4">4 - Fourth Position</option>
                </select>
                <small className="admin-form-hint">
                  Used in Manual Order mode. Assigning an occupied slot replaces the current article in that position.
                </small>
              </div>

              <div className="admin-form-group">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                  />
                  Active (visible on public news page)
                </label>
              </div>

              <div className="admin-form-actions">
                <button type="button" className="admin-button secondary" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="admin-button primary" disabled={submitting || uploadingImage}>
                  {submitting ? 'Saving...' : (editingArticle ? 'Save Changes' : 'Create Article')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;
